import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment } from "react";
import { Table } from "react-bootstrap";
import { MecordColors } from "Constants/Color";

interface SettingsTableContainerProps<TData = any> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isBordered?: boolean;
  emptyMessage?: string;
}

/**
 * Custom table container for Settings page. Mirrors Common/TableContainer
 * styling (thead background, sticky Action column, table classes) without
 * pagination, sorting, or filtering.
 */
const SettingsTableContainer = <TData,>({
  columns,
  data,
  isBordered = true,
  emptyMessage = "No Data Found",
}: SettingsTableContainerProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { getHeaderGroups, getRowModel } = table;

  return (
    <Fragment>
      <style>
        {`
        .settings-table-container.table-responsive {
          overflow-x: auto;
        }

        .settings-table-container .sticky-column {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff !important;
          border-left: 1px solid #dee2e6;
        }

        .settings-table-container thead .sticky-column {
          background-color: ${MecordColors.TableHeadingBGColor} !important;
        }

        .settings-table-container .sticky-action {
          box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
        }

        .settings-table-container .table td,
        .settings-table-container .table th {
          background-clip: padding-box;
        }

        .settings-table-container thead th {
          background-color: ${MecordColors.TableHeadingBGColor} !important;
        }
      `}
      </style>

      <div className="table-responsive table-card settings-table-container">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0`}
          // bordered={isBordered}
          responsive
          // hover
        >
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn =
                    index === headerGroup.headers.length - 1;
                  const isActionColumn =
                    (header.column.columnDef.id ?? header.column.id) === "action";

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${
                        isLastColumn && isActionColumn
                          ? "sticky-column sticky-action"
                          : ""
                      }${isBordered ? " table-bordered" : ""}`}
                      style={
                        isLastColumn && isActionColumn
                          ? { minWidth: 100, width: 100 }
                          : undefined
                      }
                    >
                      {!header.isPlaceholder &&
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-muted py-4"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1;
                    const isActionColumn =
                      (cell.column.columnDef.id ?? cell.column.id) === "action";

                    return (
                      <td
                        key={cell.id}
                        className={
                          isLastColumn && isActionColumn
                            ? "sticky-column sticky-action"
                            : ""
                        }
                        style={
                          isLastColumn && isActionColumn
                            ? { minWidth: 100, width: 100 }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </Fragment>
  );
};

export default SettingsTableContainer;
